import type {
  GetPricesResponse,
  CreateMembershipCheckoutResponse,
  CreateFreeTrialResponse,
  UserMembershipStatusResponse,
  PaymentsSettingResponse,
  PaymentsSettingsResponse,
  ExternalMembershipsResponse,
  RedeemExternalMembershipResponse,
  PaymentLinksResponse,
  CreatePaymentsSettingRequest,
  UpdatePaymentsSettingRequest,
  CreatePaymentLinkRequest,
  CreatePaymentLinkResponse,
  MembershipDuration,
} from "../types/payments";
import config from "../config/config";
import axios from "../util/axios";

/**
 * Get all active Stripe prices
 */
export async function getPrices(): Promise<GetPricesResponse> {
  const response = await axios.get(
    `${config.defaults.api_url}/payments/stripe/prices`,
  );
  return response.data;
}

/**
 * Get user membership status
 */
export async function getUserMembershipStatus(): Promise<UserMembershipStatusResponse> {
  const response = await axios.get(
    `${config.defaults.api_url}/payments/membership/status`,
  );
  return response.data;
}

/**
 * Create membership checkout session
 * @param duration - Membership duration (monthly or yearly)
 * @param discountCode - Optional discount code
 */
export async function createMembershipCheckout(
  duration: MembershipDuration,
  discountCode?: string,
): Promise<CreateMembershipCheckoutResponse> {
  const params = new URLSearchParams({ duration });
  if (discountCode) {
    params.append("discountCode", discountCode);
  }

  const response = await axios.post(
    `${config.defaults.api_url}/payments/membership/checkout?${params.toString()}`,
  );
  return response.data;
}

/**
 * Start free trial membership
 * @param duration - Billing cycle preference for when trial ends
 */
export async function createFreeTrialMembership(
  duration: MembershipDuration,
): Promise<CreateFreeTrialResponse> {
  const params = new URLSearchParams({ duration });

  const response = await axios.post(
    `${config.defaults.api_url}/payments/membership/free-trial?${params.toString()}`,
  );
  return response.data;
}

/**
 * Get available payment links for external onboarding
 */
export async function getExternalOnboardingPaymentLinks(): Promise<PaymentLinksResponse> {
  const response = await axios.get(
    `${config.defaults.api_url}/payments/external-onboarding/payment-links`,
  );
  return response.data;
}

/**
 * Redeem external membership for authenticated user
 */
export async function redeemExternalMembership(): Promise<RedeemExternalMembershipResponse> {
  const response = await axios.post(
    `${config.defaults.api_url}/payments/external-onboarding/redeem`,
  );
  return response.data;
}

/**
 * Get all payments settings
 */
export async function getAllPaymentsSettings(): Promise<PaymentsSettingsResponse> {
  const response = await axios.get(
    `${config.defaults.api_url}/payments/settings`,
  );
  return response.data;
}

/**
 * Create a new payments setting
 * @param setting - The setting to create
 */
export async function createPaymentsSetting(
  setting: CreatePaymentsSettingRequest,
): Promise<PaymentsSettingResponse> {
  const response = await axios.post(
    `${config.defaults.api_url}/payments/settings`,
    setting,
  );
  return response.data;
}

/**
 * Update a payments setting by key
 * @param key - The setting key to update
 * @param setting - The updated setting data
 */
export async function updatePaymentsSetting(
  key: string,
  setting: UpdatePaymentsSettingRequest,
): Promise<PaymentsSettingResponse> {
  const response = await axios.put(
    `${config.defaults.api_url}/payments/settings/${key}`,
    setting,
  );
  return response.data;
}

/**
 * Get a payments setting by key
 * @param key - The setting key to retrieve
 */
export async function getPaymentsSettingByKey(
  key: string,
): Promise<PaymentsSettingResponse> {
  const response = await axios.get(
    `${config.defaults.api_url}/payments/settings/${key}`,
  );
  return response.data;
}

/**
 * Get all external memberships with optional email filter
 * @param email - Optional email filter
 */
export async function getAllExternalMemberships(
  email?: string,
): Promise<ExternalMembershipsResponse> {
  const params = email ? new URLSearchParams({ email }) : "";
  const url = `${config.defaults.api_url}/payments/external-onboarding/memberships${params ? `?${params}` : ""}`;

  const response = await axios.get(url);
  return response.data;
}

/**
 * Create a payment link
 * @param paymentLink - The payment link data to create
 */
export async function createPaymentLink(
  paymentLink: CreatePaymentLinkRequest,
): Promise<CreatePaymentLinkResponse> {
  const response = await axios.post(
    `${config.defaults.api_url}/payments/payment-links`,
    paymentLink,
  );
  return response.data;
}

/**
 * Delete a payment link by ID
 * @param paymentLinkId - The payment link ID to delete
 */
export async function deletePaymentLink(paymentLinkId: string): Promise<{
  message: string;
  error: boolean;
  data: { success: boolean };
}> {
  const response = await axios.delete(
    `${config.defaults.api_url}/payments/payment-links/${paymentLinkId}`,
  );
  return response.data;
}
